import { useNavigate, useSearch } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef } from "react";
import {
  DEFAULT_FILTERS,
  decodeFilters,
  encodeFilters,
  type ResourceFilterState,
} from "@/lib/resource-filters";

const STORAGE_KEY = "nos.resources.filters";
const FILTER_KEYS = [
  "q",
  "disc",
  "comp",
  "compMin",
  "con",
  "conMin",
  "load",
  "sol",
  "acct",
] as const;

function readStorage(): Record<string, string> | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === "object") return parsed as Record<string, string>;
    return null;
  } catch {
    return null;
  }
}

function writeStorage(encoded: Record<string, string>): void {
  try {
    if (Object.keys(encoded).length === 0) localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, JSON.stringify(encoded));
  } catch {
    // localStorage unavailable (private mode) — URL remains the source of truth.
  }
}

export function useResourceFilters(): {
  state: ResourceFilterState;
  setState: (next: ResourceFilterState) => void;
  reset: () => void;
} {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const navigate = useNavigate({ from: "/dashboard/resources" });
  const hydratedRef = useRef(false);

  const urlHasFilters = FILTER_KEYS.some((k) => typeof search[k] === "string");
  const state = useMemo(() => decodeFilters(search), [search]);

  const setState = useCallback(
    (next: ResourceFilterState) => {
      const encoded = encodeFilters(next);
      writeStorage(encoded);
      // Replace all filter keys; the resources route carries no other search params.
      navigate({ search: () => encoded, replace: true });
    },
    [navigate],
  );

  const reset = useCallback(() => setState(DEFAULT_FILTERS), [setState]);

  // Bare /dashboard/resources visit (no filter params): hydrate once from localStorage.
  useEffect(() => {
    if (hydratedRef.current) return;
    hydratedRef.current = true;
    if (urlHasFilters) return;
    const stored = readStorage();
    if (stored && Object.keys(stored).length > 0) setState(decodeFilters(stored));
  }, [urlHasFilters, setState]);

  return { state, setState, reset };
}
